/** The agency's connected accounts. Shapes mirror the /connections routes in apps/api. */

import { apiCall, orgPath } from '../../api.ts';

export type ConnectionStatus = 'PENDING_AUTH' | 'ACTIVE' | 'PAUSED' | 'ERROR' | 'REVOKED';

export interface DataSource {
  id: string;
  source_key: string;
  display_name: string;
  status: string;
}

export interface Connection {
  id: string;
  data_source_id: string;
  display_name: string;
  status: ConnectionStatus;
  last_successful_sync_at: string | null;
  created_at: string;
}

export interface SyncRun {
  id: string;
  status: string;
  records_stored: number;
  error_message: string | null;
  started_at: string;
  created_at: string;
}

const path = (org: string, rest: string) => orgPath(org, rest);

export const integrationsApi = {
  sources: (org: string) =>
    apiCall<{ sources: DataSource[] }>(path(org, '/data-sources')).then((data) => data.sources),
  connections: (org: string) =>
    apiCall<{ connections: Connection[] }>(path(org, '/connections')).then(
      (data) => data.connections,
    ),
  create: (org: string, sourceKey: string, displayName: string) =>
    apiCall<{ connection: Connection }>(path(org, '/connections'), {
      method: 'POST',
      body: JSON.stringify({ source_key: sourceKey, display_name: displayName }),
    }).then((data) => data.connection),
  storeCredential: (org: string, connectionId: string, value: Record<string, string>) =>
    apiCall<unknown>(path(org, `/connections/${connectionId}/credential`), {
      method: 'PUT',
      body: JSON.stringify({ credential_type: 'JSON', value: JSON.stringify(value) }),
    }),
  sync: (org: string, connectionId: string) =>
    apiCall<{ accepted: boolean }>(path(org, `/connections/${connectionId}/sync-runs`), {
      method: 'POST',
    }),
  lastRun: (org: string, connectionId: string) =>
    apiCall<SyncRun[]>(path(org, `/connections/${connectionId}/sync-runs?limit=1`)).then(
      (runs) => runs[0] ?? null,
    ),
  revoke: (org: string, connectionId: string) =>
    apiCall<{ revoked: boolean }>(path(org, `/connections/${connectionId}/credential/revoke`), {
      method: 'POST',
    }),
  oauthUrl: (org: string, connectionId: string, redirectUri: string, nonce: string) =>
    apiCall<{ consent_url: string }>(
      path(
        org,
        `/connections/${connectionId}/oauth/url?${new URLSearchParams({ redirect_uri: redirectUri, nonce })}`,
      ),
    ).then((data) => data.consent_url),
  exchange: (org: string, connectionId: string, code: string, redirectUri: string) =>
    apiCall<unknown>(path(org, `/connections/${connectionId}/oauth/exchange`), {
      method: 'POST',
      body: JSON.stringify({ code, redirect_uri: redirectUri }),
    }),
  setPaused: (org: string, connectionId: string, paused: boolean) =>
    apiCall<{ connection: Connection }>(
      path(org, `/connections/${connectionId}/${paused ? 'pause' : 'resume'}`),
      { method: 'POST' },
    ),
};
